import { ComplexValue, RequestApiMethod } from 'app/types-shared';
import { manageOptionsButton, manageOptionsUserGroup } from 'app/area/shared/manage-options';
import { LinkCodesTranslation } from '../constants';
import { endpoints } from '../endpoints';
import { ActionPayloadType, CodeField, ListBranchItem } from '../types-store';
import { CodeFieldWireKey } from '../types';

const userGroupSelected =
  (userGroup: ComplexValue<number>) =>
    (item: ListBranchItem) =>
      item?.userGroup?.some(itemUserGroup => itemUserGroup.value === userGroup?.value) ?? false;

const applyUserGroupSelection =
  (userGroup: ComplexValue<number>) =>
    (item: ListBranchItem, selected: boolean): ListBranchItem => {

      if (!userGroup?.value || userGroupSelected(userGroup)(item) === selected) {
        return item;
      }

      return {
        ...item,
        userGroup: selected
          ? [...(item.userGroup || []), { value: userGroup.value }]
          : (item.userGroup || []).filter(itemUserGroup => itemUserGroup.value !== userGroup?.value)
      };
    };

const caseTypeSelected =
  (caseType: ComplexValue<number>) =>
    (item: ListBranchItem) =>
      item?.caseType?.some(itemCaseType => itemCaseType.value === caseType?.value) ?? false;

const applyCaseTypeSelection =
  (caseType: ComplexValue<number>) =>
    (item: ListBranchItem, selected: boolean): ListBranchItem => {

      if (!caseType?.value || caseTypeSelected(caseType)(item) === selected) {
        return item;
      }

      return {
        ...item,
        caseType: selected
          ? [...(item.caseType || []), { value: caseType.value }]
          : (item.caseType || []).filter(itemCaseType => itemCaseType.value !== caseType?.value)
      };
    };

const typeSelected =
  (type: ComplexValue<number>) =>
    (item: ListBranchItem) =>
      item?.type?.some(itemType => itemType.value === type?.value) ?? false;

const applyTypeSelection =
  (type: ComplexValue<number>) =>
    (item: ListBranchItem, selected: boolean): ListBranchItem => {

      if (!type?.value || typeSelected(type)(item) === selected) {
        return item;
      }

      return {
        ...item,
        type: selected
          ? [...(item.type || []), { value: type.value }]
          : (item.type || []).filter(itemType => itemType.value !== type?.value)
      };
    };

const itemSelected = {
  [ActionPayloadType.userGroup]: userGroupSelected,
  [ActionPayloadType.caseType]: caseTypeSelected,
  [ActionPayloadType.type]: typeSelected
} as const;

const applySelection = {
  [ActionPayloadType.userGroup]: applyUserGroupSelection,
  [ActionPayloadType.caseType]: applyCaseTypeSelection,
  [ActionPayloadType.type]: applyTypeSelection
} as const;

const label = {
  [ActionPayloadType.userGroup]: (translation: LinkCodesTranslation) => translation.user_group,
  [ActionPayloadType.caseType]: (translation: LinkCodesTranslation) => translation.case_type,
  [ActionPayloadType.type]: (translation: LinkCodesTranslation) => translation.type
} as const;

const includeSubtables = {
  [ActionPayloadType.userGroup]: [CodeFieldWireKey.userGroup],
  [ActionPayloadType.caseType]: [CodeFieldWireKey.caseType],
  [ActionPayloadType.type]: [CodeFieldWireKey.type]
};

const endpointOptions = {
  [ActionPayloadType.userGroup]: (type: CodeField, translation: LinkCodesTranslation) => ({
    endpoint: endpoints.userGroupOptions,
    extraFooterButton: manageOptionsUserGroup(translation.manage_options)
  }),
  [ActionPayloadType.caseType]: (type: CodeField, translation: LinkCodesTranslation) => ({
    endpoint: endpoints.caseTypeOptions
  }),
  [ActionPayloadType.type]: (type: CodeField, translation: LinkCodesTranslation) => ({
    endpoint: endpoints.codetableSearch(type?.id),
    loadOptionsParams: { requestApiMethod: RequestApiMethod.post, displayNotSelected: false, apiVersion: '3.0' },
    optionsParams: { limitFieldsTo: [CodeFieldWireKey.code] },
    optionsRequestPayload: { includeInactiveCodes: { enabled: false } },
    searchRequired: type?.searchRequired,
    extraFooterButton: manageOptionsButton(translation.manage_options, type?.id)
  })
} as const;

export const activeItemSelected = (
  activeItem: ComplexValue<number>,
  type: ActionPayloadType
) => itemSelected[type](activeItem);

export const applySelectionOnActiveItem = (
  activeItem: ComplexValue<number>,
  type: ActionPayloadType
) => applySelection[type](activeItem);

export const getLabel = (
  type: ActionPayloadType,
  translation: LinkCodesTranslation
) => label[type](translation);

export const getIncludeSubtables = (
  type: ActionPayloadType
) => includeSubtables[type];

export const getEndpointOptions = (
  type: ActionPayloadType,
  metadataType: CodeField,
  translation: LinkCodesTranslation
) => endpointOptions[type](metadataType, translation);
